import { Post } from '@/types/post';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAdmin } from '@/contexts/AdminContext';
import { usePosts } from '@/contexts/PostsContext';

const CATEGORY_COLORS: Record<string, string> = {
  'Global Affairs': 'bg-teal-600',
  'International Order': 'bg-stone-700',
  'Rights and Freedoms': 'bg-rose-500',
  'Politics': 'bg-amber-500',
  'Justice and Freedom': 'bg-neutral-600',
  'Law and Society': 'bg-stone-500',
  'Uncategorized': '',
};

interface IssueCardProps {
  post: Post;
  onClick: () => void;
  onEdit?: () => void;
}

const IssueCard = ({ post, onClick, onEdit }: IssueCardProps) => {
  const { lang } = useLanguage();
  const { isAdmin } = useAdmin();
  const { deletePost } = usePosts();
  const catColor = CATEGORY_COLORS[post.category] || 'bg-stone-600';

  const totalReactions = Object.values(post.emojiReactions).reduce((a, b) => a + b, 0);
  const commentCount = post.comments.filter((c) => !c.blocked).length;
  const slideCount = post.slides.length;
  const isPrivate = !post.isPublic;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('이 게시물을 삭제할까요?')) deletePost(post.id);
  };

  return (
    <article
      onClick={onClick}
      className={`group relative cursor-pointer overflow-hidden rounded-xl bg-stone-100 aspect-square ${
        isAdmin && isPrivate ? 'ring-2 ring-red-400 ring-offset-1' : ''
      }`}
    >
      {/* Private badge for admin — always visible */}
      {isAdmin && isPrivate && (
        <div className="absolute top-2.5 left-2.5 z-20 flex items-center gap-1 bg-red-500 text-white rounded-full px-2 py-0.5 shadow-sm">
          <i className="ri-eye-off-line text-[10px]" />
          <span className="text-[10px] font-bold">{lang === 'ko' ? '비공개' : 'Private'}</span>
        </div>
      )}
      {/* Cover image */}
      <img
        src={post.slides[0] || post.coverImage}
        alt={post.title[lang]}
        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
      />

      {/* Multiple slides indicator — top right */}
      {slideCount > 1 && (
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white rounded-full px-2 py-1">
          <i className="ri-gallery-line text-xs" />
          <span className="text-[10px] font-bold">{slideCount}</span>
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-all duration-300 flex flex-col justify-between p-3">
        {/* Top row on hover */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-start justify-between">
          {post.category !== 'Uncategorized' ? (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${catColor} whitespace-nowrap`}>
              {post.category}
            </span>
          ) : (
            <span />
          )}
          {isAdmin && (
            <button
              onClick={handleDelete}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500/80 text-white hover:bg-red-600 cursor-pointer transition-colors"
            >
              <i className="ri-delete-bin-line text-xs" />
            </button>
          )}
        </div>

        {/* Bottom row on hover */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <p className="text-white text-xs font-semibold leading-snug line-clamp-2 mb-2">
            {post.title[lang]}
          </p>
          <div className="flex items-center gap-3">
            {totalReactions > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-sm leading-none">❤️</span>
                <span className="text-white/80 text-xs">{totalReactions}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-chat-3-line text-white/80 text-xs" />
              </div>
              <span className="text-white/80 text-xs">{commentCount}</span>
            </div>
            {/* Slide dots preview */}
            {slideCount > 1 && (
              <div className="ml-auto flex items-center gap-0.5">
                {Array.from({ length: Math.min(slideCount, 5) }).map((_, i) => (
                  <div key={i} className={`rounded-full ${i === 0 ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/50'}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default IssueCard;
